// ============================================
// FILE: stateManagers/stores/studentProfileStore.ts
// ============================================
import { create } from 'zustand';
import apiClient from '../apiSetup/axiosInterceptor';
import type { PushToken } from './teacherProfileStore';

interface ProfilePicture {
  file_name: string;
  key: string;
  cdn_url: string;
  dev_url: string;
  mime_type: string;
  size: number;
  etag: string | null;
  uploaded_at: string | null;
}

interface Institute {
  institute_id: string;
  name: string;
  address: string;
  contact_no: string;
  email: string;
}

interface Branch {
  branch_id: string;
  name: string;
  address: string;
  contact_no: string | null;
  email: string;
}

interface Enrollment {
  enrollment_id: string;
  enrollment_no: string | null;
  course_id: string | null;
  course_name: string | null;
  course_title: string | null;
  batch_id: string | null;
  batch_name: string | null;
  batch_time: string | null;
  start_month: string | null;
  start_year: number | null;
  end_month: string | null;
  end_year: number | null;
  date_of_admission: string | null;
  created_at: string | null;
}

export interface StudentProfile {
  // Basic Information
  student_id: string;
  student_custom_id: string | null;
  name: string;
  dateOfBirth: string;
  gender: string;
  aadharNumber: string;
  category: string;
  mobileNumber: string;
  email: string;
  district: string;
  state: string;
  pinCode: string;

  // Optional Personal Details
  bloodGroup: string | null;
  whatsappNumber: string | null;
  villageTown: string | null;
  wardNo: string | null;
  tehsil: string | null;
  previousIllness: string | null;

  // Parent Contact Details
  fatherMobile: string | null;
  fatherWhatsapp: string | null;
  motherMobile: string | null;
  motherWhatsapp: string | null;

  // Education History
  previousClass: string | null;
  yearOfPassing: string | null;
  schoolBoard: string | null;
  totalMarks: number | null;
  marksObtained: number | null;

  // Family Information
  fatherName: string | null;
  fatherOccupation: string | null;
  fatherIncome: number | null;
  motherName: string | null;
  motherOccupation: string | null;
  motherIncome: number | null;

  // Profile Picture
  profilePicture: ProfilePicture | null;

  // System Fields
  status: string;
  created_by: string | null;
  user_id: string;
  created_at: string;

  // Related Data
  institute: Institute;
  branch: Branch;

  // Enrollments
  enrollments: Enrollment[];
  is_enrolled: boolean;
  total_courses: number;

  // Push tokens
  push_tokens: PushToken[];
  total_devices: number;
}

interface StudentProfileStore {
  profile: StudentProfile | null;
  isLoading: boolean;
  error: string | null;

  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<StudentProfile>) => void;
  clearProfile: () => void;
  setError: (error: string | null) => void;

  // Push token helpers
  getPushTokens: () => PushToken[];
  hasPushToken: (token: string) => boolean;
  updatePushTokens: (tokens: PushToken[]) => void;
}

export const useStudentProfileStore = create<StudentProfileStore>((set, get) => ({
  profile: null,
  isLoading: false,
  error: null,

  fetchProfile: async () => {
    set({ isLoading: true, error: null });

    try {
      // apiClient interceptor handles Bearer token from cookies automatically
      const response = await apiClient.get('/v1/student/profile');

      if (response.data.success) {
        set({
          profile: response.data.data,
          isLoading: false,
          error: null,
        });
        console.log(response.data)
      } else {
        set({ error: 'Failed to fetch profile', isLoading: false });
      }
    } catch (error: any) {
      // apiClient interceptor already handles 401 → token refresh → redirect
      if (error.code === 'ERR_NETWORK' || error.isNetworkError) {
        set({ error: 'No internet connection', isLoading: false });
      } else {
        set({
          error: error.response?.data?.message || 'Failed to fetch profile',
          isLoading: false,
        });
      }
    }
  },

  updateProfile: (data) => {
    set((state) => ({
      profile: state.profile ? { ...state.profile, ...data } : null,
    }));
  },

  clearProfile: () => {
    set({ profile: null, error: null, isLoading: false });
  },

  setError: (error) => {
    set({ error });
  },

  // ── Push token helpers ─────────────────────────────────────────────────────

  getPushTokens: () => get().profile?.push_tokens ?? [],

  hasPushToken: (token: string) => {
    const tokens = get().profile?.push_tokens ?? [];
    return tokens.some((t) => t.token === token);
  },

  updatePushTokens: (tokens: PushToken[]) => {
    set((state) => ({
      profile: state.profile
        ? { ...state.profile, push_tokens: tokens, total_devices: tokens.length }
        : null,
    }));
  },
}));
